import { MusicPlayer } from "./MusicPlayer";
import { generateSfxLibrary } from "./sfx";
import { SfxPool } from "./SfxPool";

export type SfxName = "eat" | "splash" | "alert";

export type AudioContextFactory = () => AudioContext;

export interface AudioControllerOptions {
  readonly enabled?: boolean;
  readonly contextFactory?: AudioContextFactory;
  readonly musicPlayer?: MusicPlayer;
}

export class AudioController {
  private readonly contextFactory: AudioContextFactory;
  private readonly music: MusicPlayer;
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfx: SfxPool | null = null;
  private enabled: boolean;
  private unlocked = false;

  constructor(options: AudioControllerOptions = {}) {
    this.contextFactory = options.contextFactory ?? defaultContextFactory;
    this.music = options.musicPlayer ?? new MusicPlayer();
    this.enabled = options.enabled ?? true;
    this.music.setMuted(!this.enabled);
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  get isUnlocked(): boolean {
    return this.unlocked;
  }

  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (this.master !== null) this.master.gain.value = enabled ? 1 : 0;
    this.music.setMuted(!enabled);
    if (!enabled) this.music.pause();
  }

  async unlock(): Promise<void> {
    if (this.unlocked) return;
    if (this.context === null) {
      const ctx = this.contextFactory();
      const master = ctx.createGain();
      master.gain.value = this.enabled ? 1 : 0;
      master.connect(ctx.destination);
      const sfx = new SfxPool(ctx, master);
      generateSfxLibrary(ctx, sfx);
      this.context = ctx;
      this.master = master;
      this.sfx = sfx;
    }
    if (this.context.state === "suspended") {
      try {
        await this.context.resume();
      } catch {
        return;
      }
    }
    this.unlocked = true;
  }

  playSfx(name: SfxName): void {
    if (!this.enabled || !this.unlocked || this.sfx === null) return;
    this.sfx.play(name);
  }

  loadMusic(url: string): void {
    this.music.load(url);
  }

  async startMusic(): Promise<void> {
    if (!this.enabled || !this.unlocked) return;
    await this.music.play();
  }

  stopMusic(): void {
    this.music.pause();
  }

  dispose(): void {
    this.music.dispose();
    if (this.context !== null) {
      void this.context.close().catch(() => undefined);
      this.context = null;
      this.master = null;
      this.sfx?.dispose();
      this.sfx = null;
    }
    this.unlocked = false;
  }
}

function defaultContextFactory(): AudioContext {
  return new AudioContext();
}
